// Pure email helpers — safe to import from BOTH server and client code.
// Intentionally NO "use client" directive: these are plain functions with no
// React/browser dependency. Keeping them in a server/client-shared module lets
// the Meridian server email-delivery path call appendSignatureToBody without
// tripping the Next.js "client function called from server" boundary error.

import { escapeHtmlText } from "@/lib/email/body-utils/encoding";
import { type EmailBodyFormat, looksLikeHtmlBody, resolveBodyIsHtml } from "./body-format";

/**
 * Append signature to body if not already present
 */
export function appendSignatureToBody(
  body: string,
  signature?: { id: string; content: string }
): string {
  if (!signature?.content) return body;
  if (body.includes(signature.content)) return body;
  return `${body}\n\n${signature.content}`;
}

/**
 * Convert a plain-text body into a minimal HTML fragment: HTML-escape it, turn
 * newlines into <br>, and wrap in a <div>. Mirrors
 * appendWtcProfessionalSignatureToMessageBody so a plain-text body's line breaks
 * survive when the message is upgraded to HTML.
 */
export function plainTextToHtmlFragment(text: string): string {
  const escaped = escapeHtmlText(text).replace(/\n/g, "<br>");
  return escaped ? `<div>${escaped}</div>` : "";
}

export interface SignatureAppendResult {
  /** Combined body (message + signature), ready for the send route. */
  body: string;
  /** The format the COMBINED message must ship in — authoritative for the send. */
  bodyFormat: EmailBodyFormat;
}

/**
 * Append a signature AND resolve the format the combined message must ship in.
 *
 * A signature carries its own format independent of the body. A caller's
 * declared `bodyFormat` governs only the body THEY wrote — it must never force
 * an appended HTML signature back to plaintext, or the raw <div>…</div> markup
 * is delivered as literal text (the exact bug this fixes: an AI/MCP send with a
 * plain-text body declared bodyFormat="text", then the user's HTML signature was
 * concatenated on and shipped un-rendered).
 *
 * Rules:
 *   - No / empty signature → no-op append; format follows the body.
 *   - Signature already present in body → don't double-append; format still
 *     accounts for the signature's HTML.
 *   - Body HTML (declared or detected) → append as-is, ship "html".
 *   - Body plain text + HTML signature → HTML-ize the body (so its newlines
 *     render), join with an HTML separator, ship "html".
 *   - Body plain text + plain-text/no signature → append as-is, ship "text".
 */
export function appendSignatureResolvingFormat(
  body: string,
  bodyFormat: EmailBodyFormat | undefined,
  signature?: { id: string; content: string }
): SignatureAppendResult {
  const bodyIsHtml = resolveBodyIsHtml(body, bodyFormat);
  const sig = signature?.content;
  const sigIsHtml = typeof sig === "string" && sig.length > 0 ? looksLikeHtmlBody(sig) : false;

  if (!sig) {
    return { body, bodyFormat: bodyIsHtml ? "html" : "text" };
  }

  if (body.includes(sig)) {
    return { body, bodyFormat: bodyIsHtml || sigIsHtml ? "html" : "text" };
  }

  if (!bodyIsHtml && !sigIsHtml) {
    // Both plain text — preserve the original append behavior exactly.
    return { body: `${body}\n\n${sig}`, bodyFormat: "text" };
  }

  // The combined message ships as HTML. If the body itself isn't HTML, convert
  // it so its line breaks survive instead of collapsing under HTML whitespace.
  const htmlBody = bodyIsHtml ? body : plainTextToHtmlFragment(body);
  const separator = htmlBody ? "<br><br>" : "";
  return { body: `${htmlBody}${separator}${sig}`, bodyFormat: "html" };
}
